// OB code refactored by CH. 😅
import { motion } from "motion/react";
import PropTypes from "prop-types";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";

import styled, { css } from "styled-components/macro";
import { noUnderline } from "theme";

import classNames from "classnames";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrLoadingBricks from "UI/FnrLoadingBricks2";

import { colorIsLight, hexToRgb } from "Utilities/Colors/colorUtils";
import useResizeObserver2 from "Utilities/Hooks/useResizeObserver2";

const FnrShineButton = forwardRef(
  (
    {
      onClick,
      children,
      borderRadius = 24,
      shineColor = "#FFFFFF",
      inset = 0,
      tapScale = 0.9,
      isActive = false,
      lit = false,
      loading = false,
      loadingColor = "#FFFFFF",
      className,
      ...props
    },
    buttonRef
  ) => {
    const shineRef = useRef();
    const measurements = useRef();

    const measureButton = useCallback(() => {
      if (!shineRef.current) return;
      measurements.current = shineRef.current.getBoundingClientRect();
      setMeasured(true);
    }, []);

    /* The observer, not a mount effect: every button on a page paid a forced
       reflow in the same commit for a radius nobody sees until hover (and
       hoverStart re-measures then anyway). The observer's initial delivery
       lands after layout, and the radius follows size changes for free. */
    useResizeObserver2(shineRef, measureButton);

    const [measured, setMeasured] = useState(false);
    const [tapping, setTapping] = useState(false);

    const hoverStart = useCallback(
      (e) => {
        measureButton();
      },
      [measureButton]
    );

    const updatePointer = useCallback(({ clientX, clientY, ...rest }) => {
      shineRef.current.style.setProperty(
        "--fnrShineX",
        `${
          ((clientX - measurements.current.left) / measurements.current.width) *
          100
        }%`
      );
      shineRef.current.style.setProperty(
        "--fnrShineY",
        `${
          ((clientY - measurements.current.top) / measurements.current.height) *
          100
        }%`
      );
    }, []);

    const tapStart = useCallback(() => setTapping(true), []);
    const tapEnd = useCallback(() => setTapping(false), []);

    const shineRGB = useMemo(
      () =>
        typeof shineColor === "string" ? hexToRgb(shineColor) : shineColor,
      [shineColor]
    );

    /* Which way up the gradient goes isn't a caller's decision, it's a fact
       about their colour — see Shine. Only a hex can be asked; a caller handing
       an {r,g,b} straight in gets the light-colour arrangement, which is the
       one that was here before this existed. */
    const darkShine =
      typeof shineColor === "string" && !colorIsLight(shineColor);

    const style = useMemo(
      () => ({
        "--fnrShineX": "50%",
        "--fnrShineY": "50%",
        "--fnrShineColorR": shineRGB.r,
        "--fnrShineColorG": shineRGB.g,
        "--fnrShineColorB": shineRGB.b,
        "--fnrShineBorderRadius": `${borderRadius}px`,
        "--fnrShineInset": inset,
        /* Off the SHORT side, so the lit spot is a size of its own rather than
           a share of the button. Taken off the long one, a wide button got a
           gradient wider than itself and came out evenly lit — the pointer had
           nothing left to move. The floor is what most buttons actually land
           on, since almost nothing here is more than 80 across its narrow
           way. */
        "--fnrShineRadius": measured
          ? `${
              Math.max(
                80,
                Math.min(
                  measurements.current.width,
                  measurements.current.height
                )
              ) * 2
            }px`
          : undefined,
        backgroundColor: isActive ? "rgba(255, 255, 255, 0.15)" : undefined,
      }),
      [
        borderRadius,
        inset,
        shineRGB.b,
        shineRGB.g,
        shineRGB.r,
        measured,
        isActive,
      ]
    );

    const handleClick = (e) => {
      if (loading) return;
      if (onClick) onClick(e);
    };

    return (
      <SFnrButtonWrapper
        onFocus={hoverStart}
        ref={buttonRef}
        onClick={handleClick}
        style={style}
        aria-busy={loading || undefined}
        className={classNames(className, { loading })}
        {...props}
      >
        <FnrShineButtonWrapper
          tabIndex={-1}
          onHoverStart={loading ? undefined : hoverStart}
          onTapStart={loading ? undefined : tapStart}
          onTapCancel={loading ? undefined : tapEnd}
          onTap={loading ? undefined : tapEnd}
          onMouseMove={loading ? undefined : updatePointer}
          ref={shineRef}
          style={style}
          className={classNames({ loading })}
        >
          <ShineWrapper>
            <Border $lit={lit && !loading} />
            <Shine $darkShine={darkShine} $lit={lit && !loading} />
          </ShineWrapper>

          <ButtonContent
            animate={{
              scale: !loading && tapping ? tapScale : 1,
            }}
          >
            <FnrLoadingBricks loading={loading} color={loadingColor}>
              {children}
            </FnrLoadingBricks>
          </ButtonContent>
        </FnrShineButtonWrapper>
      </SFnrButtonWrapper>
    );
  }
);

export const FnrShineButtonWrapper = styled(motion.div)`
  cursor: pointer;
  position: relative;
  border-radius: var(--fnrShineBorderRadius);
`;

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  border-radius: var(--fnrShineBorderRadius);

  /* This is an anchor about as often as a button now, and an as= swaps
     FnrButtonWrapper out, so nothing underneath is taking the underline off.
     It rides the button and not a seat around it — the change log clones its
     pill and flies it to the chrome, and a clone that left its seat behind
     picks the underline back up halfway across. */
  ${noUnderline}

  &:focus-visible {
    background-color: transparent;
  }

  &.loading {
    cursor: wait;
  }
`;

const ButtonContent = styled(motion.div)`
  z-index: 20;
  user-select: none;
  position: relative;
`;

const ShineWrapper = styled.div`
  position: absolute;
  inset: var(--fnrShineInset);
  border-radius: var(--fnrShineBorderRadius);
  z-index: 10;
  overflow: hidden;
`;

const litBorder = css`
  box-shadow: inset
    rgba(
      var(--fnrShineColorR),
      var(--fnrShineColorG),
      var(--fnrShineColorB),
      0.3
    )
    0 0 3px;
  transition: box-shadow 0.2s;
`;

const Border = styled.div`
  border-radius: var(--fnrShineBorderRadius);
  position: absolute;
  inset: 0;
  z-index: 5;
  transition: box-shadow 1.2s;
  box-shadow: inset
    rgba(var(--fnrShineColorR), var(--fnrShineColorG), var(--fnrShineColorB), 0)
    0 0 1px;

  /* The focused element by shape and not by tag. Callers swap the element out
     and this is an anchor about as often as a button now, and naming the tag
     quietly left every one of those with no shine on the keyboard at all.
     Nothing else in here is a direct parent of the wrapper. */
  ${FnrShineButtonWrapper}:not(.loading):hover &,
  :focus-visible > ${FnrShineButtonWrapper}:not(.loading) & {
    ${litBorder}
  }

  ${({ $lit }) => $lit && litBorder}
`;

/* The bright part follows the cursor either way — that's what a shine is, and
   it's what the two arrangements here are both for. A light shineColor pools
   under the pointer and IS the bright part. A dark one can't, so it pools
   everywhere the pointer isn't and the bright part is the gap it leaves.
   Painting a dark colour under the cursor tracks a smudge across the button,
   which is the one thing this must never do. */
const SHINE_RGB =
  "rgb(var(--fnrShineColorR), var(--fnrShineColorG), var(--fnrShineColorB))";

const shineStops =
  (fade) =>
  ({ $darkShine }) =>
    $darkShine
      ? `transparent, ${SHINE_RGB} ${fade}`
      : `${SHINE_RGB}, transparent ${fade}`;

const litShine = css`
  opacity: 0.2;
  transition: opacity 0.4s, background 0.2s;
`;

const Shine = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    var(--fnrShineRadius) circle at var(--fnrShineX) var(--fnrShineY),
    ${shineStops("70%")}
  );
  /* dumb old safari */
  @supports not (aspect-ratio: auto) {
    background: radial-gradient(
      calc(var(--fnrShineRadius) * 5) circle at var(--fnrShineX)
        var(--fnrShineY),
      ${shineStops("40%")}
    );
  }
  opacity: 0;
  transition: opacity 0.8s, background 0.2s;
  z-index: 3;

  ${FnrShineButtonWrapper}:not(.loading):hover &,
  :focus-visible > ${FnrShineButtonWrapper}:not(.loading) & {
    ${litShine}
  }

  ${({ $lit }) => $lit && litShine}
`;

FnrShineButton.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node,
  borderRadius: PropTypes.number,
  shineColor: PropTypes.string,
  inset: PropTypes.number,
  tapScale: PropTypes.number,
  isActive: PropTypes.bool,
  // the hover look without the pointer, for a row the keyboard points at from somewhere else
  lit: PropTypes.bool,
  loading: PropTypes.bool,
  loadingColor: PropTypes.string,
  className: PropTypes.string,
};

export default FnrShineButton;
